import {
  COMBO_ANIM_DEFAULT_DIRECTION,
  COMBO_ANIM_DEFAULT_SPEED,
  COMBO_ANIM_SPEEDS,
  DISABLED_ANIMATION_CONTAINERS,
} from "@library/constants";
import { throttle } from "@library/shared";
import { type RefObject, useEffect } from "react";

type AnimationPayload = {
  ref: RefObject<HTMLDivElement>;
  type?: string;
  speed?: string;
  direction?: string;
  animate: boolean;
  name: string;
};

// Constants
const BLOCK_TYPE = "block";
const ELEMENTS_TYPE = "elements";
const SET_WRAPPER = ".set-wrapper-XFGzfwO910 .setInnerWrapper";
const ELEMENT_WRAPPER = ".element-wrapper-XNCgh1eTFF";
const ANIMATED = "data-animated";
const TRUE = "true";
const BLOCK_ANIMATION = ["Array"];

// Helper functions
const isBlockType = (type?: string) => type === BLOCK_TYPE;
const isElementsType = (type?: string) => type === ELEMENTS_TYPE;

// Main functions
export const useAnimation = ({
  ref,
  type,
  speed,
  direction,
  animate,
  name,
}: AnimationPayload) => {
  useEffect(() => {
    if (!ref.current || !animate) return;
    if (isBlockType(type)) observeBlockSets(getSets(ref.current, name));
    else observeElements(ref.current, name);
    initMutationObserver(ref.current, name, type);
  }, [ref, type, speed, direction, animate, name]);
};

export const isElementInDisabledContainer = (
  element: HTMLDivElement | null,
  animation: boolean,
): boolean => {
  if (!animation || !element) return true;
  for (let i = 0; i < DISABLED_ANIMATION_CONTAINERS.length; i++) {
    if (element.closest(DISABLED_ANIMATION_CONTAINERS[i])) {
      return true;
    }
  }
  return false;
};

// ========== BLOCK OBSERVER ==========
const setBlockObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      // For very tall elements (taller than viewport), trigger at 15% visibility
      // For normal elements, prefer 50% visibility
      const isTallElement =
        entry.boundingClientRect.height > window.innerHeight;
      const shouldTrigger =
        entry.intersectionRatio >= 0.5 ||
        (isTallElement && entry.intersectionRatio >= 0.15);
      if (shouldTrigger) {
        entry.target.setAttribute(ANIMATED, TRUE);
        setBlockObserver.unobserve(entry.target);
      }
    });
  },
  {
    threshold: [0, 0.15, 0.2, 0.5],
  },
);

const getSets = (node: Element, name: string) =>
  node.querySelectorAll(setSelector(name));

const setSelector = (name: string | null) => {
  if (!name) return SET_WRAPPER;
  return SET_WRAPPER;
};

const observeBlockSets = (sets: NodeListOf<Element>) => {
  sets.forEach((set) => {
    set.removeAttribute(ANIMATED);
    setBlockObserver.observe(set);
  });
};

// ========== BLOCK OBSERVER ==========

// ========== ELEMENT OBSERVER ==========
const observeElements = (node: Element, name: string) => {
  const sets = getSets(node, name);
  const elements = Array.from(node.querySelectorAll(ELEMENT_WRAPPER));
  node.removeAttribute(ANIMATED);
  sets.forEach((set) => set.removeAttribute(ANIMATED));
  elements.forEach((element) => element.removeAttribute(ANIMATED));
  if (sets[0]) comboObserver.observe(sets[0]);
};

const comboObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      // For very tall elements (taller than viewport), trigger at 15% visibility
      // For normal elements, prefer 50% visibility
      const isTallElement =
        entry.boundingClientRect.height > window.innerHeight;
      const shouldTrigger =
        entry.intersectionRatio >= 0.5 ||
        (isTallElement && entry.intersectionRatio >= 0.15);
      if (!shouldTrigger) return;

      const set = entry.target as HTMLElement;
      const combo = set.closest('[data-layer-type="combo"]');
      if (!combo) return comboObserver.unobserve(set);
      const duration = combo.getAttribute("data-combo-animation-speed");
      const directionAttr = combo.getAttribute(
        "data-combo-animation-direction",
      );
      const delay = COMBO_ANIM_SPEEDS[duration || COMBO_ANIM_DEFAULT_SPEED];
      const direction = directionAttr || COMBO_ANIM_DEFAULT_DIRECTION;
      const name = combo.getAttribute("data-combo-name");
      comboObserver.unobserve(set);

      if (BLOCK_ANIMATION.includes(name || "")) {
        const sets = Array.from(getSets(combo, name || ""));
        const arr = direction === "backwards" ? [...sets].reverse() : [...sets];
        arr.forEach((set, index) => {
          setTimeout(() => {
            set.setAttribute(ANIMATED, TRUE);
            if (index === arr.length - 1) combo.setAttribute(ANIMATED, TRUE);
          }, index * delay);
        });
      } else {
        const elements = Array.from(combo.querySelectorAll(ELEMENT_WRAPPER));
        elements.forEach((element, index) => {
          setTimeout(() => {
            element.setAttribute(ANIMATED, TRUE);
            if (index === elements.length - 1)
              element.setAttribute(ANIMATED, TRUE);
          }, index * delay);
        });
      }
    });
  },
  {
    threshold: [0, 0.15, 0.2, 0.5],
  },
);

// ========== ELEMENT OBSERVER ==========

// ========= COMBO MUTATION OBSERVER =========
const initMutationObserver = (node: Element, name: string, type?: string) => {
  const throttledMutationObserver = throttle((mutations: MutationRecord[]) => {
    mutations.forEach((mutation) => {
      // if (mutation?.type !== 'childList') return;
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          if (isElementsType(type)) observeElements(node, name);
          if (isBlockType(type)) {
            const sets = node.querySelectorAll(
              `${SET_WRAPPER}:not([${ANIMATED}])`,
            );
            observeBlockSets(sets);
          }
        }
      });
    });
  }, 250);
  const comboMutationObserver = new MutationObserver(throttledMutationObserver);
  comboMutationObserver.observe(node, {
    attributes: true,
    childList: true,
    subtree: true,
  });
};
// ========= COMBO MUTATION OBSERVER =========
